/**
 * PDP review slider.
 *
 * Nothing about the children is cached. This block can sit inside the product section, which is
 * morphed in place when the version changes — the element survives while its children are
 * replaced, so a stored list would describe the previous product's reviews. The lists are read
 * live and the handlers are delegated to the host.
 */
(() => {
  if (customElements.get('vp-pdp-review-slider')) return;

  class VpPdpReviewSlider extends HTMLElement {
    #timer = null;
    #controller = null;

    get track() { return this.querySelector('.vp-rvs__track'); }
    get slides() { return [...this.querySelectorAll('.vp-rvs__slide')]; }
    get dots() { return [...this.querySelectorAll('.vp-rvs__dot')]; }

    connectedCallback() {
      this.index = 0;
      const { signal } = (this.#controller = new AbortController());

      this.addEventListener('click', (event) => {
        const arrow = event.target.closest?.('.vp-rvs__arrow');
        if (arrow) {
          this.#move(Number(arrow.dataset.step));
          this.#stop();
          return;
        }
        const dot = event.target.closest?.('.vp-rvs__dot');
        if (!dot) return;
        this.show(this.dots.indexOf(dot));
        this.#stop();
      }, { signal });

      this.addEventListener('keydown', (event) => {
        if (!event.target.closest?.('.vp-rvs__dot')) return;
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        this.#move(step);
        this.dots[this.index]?.focus();
        this.#stop();
      }, { signal });

      this.#addSwipe(signal);
      this.#start();

      // Pause while the pointer rests on the card, so a review cannot slide out mid-sentence.
      this.addEventListener('pointerenter', () => this.#stop(), { signal });
      this.addEventListener('pointerleave', () => this.#start(), { signal });
    }

    disconnectedCallback() {
      this.#stop();
      this.#controller?.abort();
    }

    show(i) {
      const slides = this.slides;
      if (i < 0 || i >= slides.length || i === this.index) return;
      this.index = i;
      this.track?.style.setProperty('--vp-rvs-i', String(i));
      for (const [n, dot] of this.dots.entries()) {
        dot.classList.toggle('is-active', n === i);
        dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
      }
    }

    /** Wraps at both ends, so arrows and swipes never run out of reviews. */
    #move(step) {
      const count = this.slides.length;
      if (count < 2 || !step) return;
      this.show((this.index + step + count) % count);
    }

    #start() {
      if (this.dataset.autoplay !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const count = this.slides.length;
      if (count < 2) return;
      this.#stop();
      const seconds = Number(this.dataset.interval) || 6;
      this.#timer = setInterval(() => this.show((this.index + 1) % this.slides.length), seconds * 1000);
    }

    #stop() {
      if (this.#timer) clearInterval(this.#timer);
      this.#timer = null;
    }

    /** Horizontal drags move the slider; vertical ones are left to the page. */
    #addSwipe(signal) {
      let x0 = null;
      let y0 = null;
      this.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; }, { signal });
      this.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        this.#move(dx < 0 ? 1 : -1);
        this.#stop();
      }, { signal });
      this.addEventListener('pointercancel', () => { x0 = null; }, { signal });
    }
  }

  customElements.define('vp-pdp-review-slider', VpPdpReviewSlider);
})();
